// The customer and environment a tab is working in.
//
// sessionStorage is per tab, so switching environment in one tab no longer redirects another
// tab's reads and saves. localStorage keeps the last value used anywhere, only to seed a tab
// that has none yet (a newly opened tab).

const scopeKeys = {
  environmentId: "environment_id",
  customerId: "customer_id",
  envList: "envList",
} as const;

type ScopeKey = typeof scopeKeys[keyof typeof scopeKeys];

const getStorage = (kind: "sessionStorage" | "localStorage"): Storage | undefined => {
  try {
    return typeof window !== "undefined" ? window[kind] : undefined;
  } catch {
    return undefined;
  }
};

const safeRead = (storage: Storage | undefined, key: string): string | null => {
  if (!storage) {
    return null;
  }

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (storage: Storage | undefined, key: string, value: string | null): void => {
  if (!storage) {
    return;
  }

  try {
    if (value === null) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, value);
    }
  } catch {
    // no-op: storage might be unavailable (Safari private mode, etc.)
  }
};

const readScope = (key: ScopeKey): string | null => {
  const tabValue = safeRead(getStorage("sessionStorage"), key);
  if (tabValue !== null) {
    return tabValue;
  }

  const lastUsed = safeRead(getStorage("localStorage"), key);
  if (lastUsed !== null) {
    safeWrite(getStorage("sessionStorage"), key, lastUsed);
  }
  return lastUsed;
};

const writeScope = (key: ScopeKey, value: string | null): void => {
  safeWrite(getStorage("sessionStorage"), key, value);
  safeWrite(getStorage("localStorage"), key, value);
};

const toValue = (id?: number | string | null): string | null =>
  id === undefined || id === null || id === "" ? null : String(id);

export const getScopeEnvironmentId = (): string | null => readScope(scopeKeys.environmentId);

export const getScopeCustomerId = (): string | null => readScope(scopeKeys.customerId);

export const getScopeEnvList = (): string | null => readScope(scopeKeys.envList);

export const setScopeEnvironmentId = (id?: number | string | null): void =>
  writeScope(scopeKeys.environmentId, toValue(id));

export const setScopeCustomerId = (id?: number | string | null): void =>
  writeScope(scopeKeys.customerId, toValue(id));

export const setScopeEnvList = (json?: string | null): void =>
  writeScope(scopeKeys.envList, json ?? null);

// Which id a tab should be on after (re)loading the available ids. The backend's "current" id is
// whatever the user last picked in any tab, so it only applies when this tab's own choice is gone.
export const pickActiveScopeId = (
  availableIds: number[],
  tabChoice: string | null,
  userCurrentId?: number
): number | undefined => {
  const tabId = tabChoice ? Number(tabChoice) : NaN;
  if (availableIds.includes(tabId)) {
    return tabId;
  }

  if (userCurrentId !== undefined && availableIds.includes(userCurrentId)) {
    return userCurrentId;
  }

  return availableIds[0];
};
